import { ImageAsset } from "@/components/ImageAsset";
import LinkStyled from "@/components/LinkStyled";
import { PauseIconCircle, PlayIconCircle } from "@/components/icons";
import {
  actualTrackState,
  playerCommandState,
  playerIsPlayingState,
} from "@/context/states";
import { responsiveImage } from "@/lib/helper";
import { basierCirleBold, basierCirleRegular } from "@/styles/fonts";
import CloseIcon from "@mui/icons-material/Close";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Fade } from "@mui/material";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import SvgIcon from "@mui/material/SvgIcon";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { useRecoilState } from "recoil";
import theme from "../styles/theme";
import { BandMessageShareLikeButtons } from "./music/BandMessageShareLikeButtons";

export const PlaylistGrid = ({ song, variant, onPlaySong, ref, ...props }) => {
  const [actualTrack] = useRecoilState(actualTrackState);
  const [playerIsPlaying] = useRecoilState(playerIsPlayingState);
  const [playerCommand, setPlayerCommand] = useRecoilState(playerCommandState);
  const [buttonsOpen, setButtonsOpen] = useState(false);

  const handlePlaySong = (song) => {
    if (actualTrack?.id === song.id) {
      setPlayerCommand({ command: "pause" });
    } else {
      //setPlayerCommand({ command: "play" });
      if (onPlaySong) {
        onPlaySong(song);
      }
    }
  };

  const overlayBox = {
    position: "absolute",
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    width: "100%",
    height: "100%",
    zIndex: "500",
    background:
      actualTrack?.id === song?.id
        ? "linear-gradient(0deg, rgba(245,157,49,0.97) 0%, rgba(245,157,49,0.97) 100%)"
        : "linear-gradient(0deg, rgba(126,105,238,0.97) 0%, rgba(126,105,238,0.97) 100%)",
  };

  return (
    <>
      <Card
        elevation={0}
        variant={
          actualTrack?.id === song?.id
            ? "PlaylistGridCardPlay"
            : "PlaylistGridCard"
        }
        sx={{
          height: "100%",
          position: "relative",
          overflow: "visible",
          color: theme.palette.common.white,
          p: 3,
          transition: "all .2s ease-in-out",
        }}
      >
        <Box className="PlaylistGridCard--GradientOverlay"></Box>
        <ImageAsset
          url={responsiveImage(song.band.photo?.formats, 830)}
          layout="fill"
        />

        {buttonsOpen ? (
          <Fade in={buttonsOpen}>
            <Box ref={ref} {...props} sx={overlayBox}>
              <Grid
                container
                justifyContent="center"
                alignItems="center"
                sx={{ height: "100%" }}
              >
                <BandMessageShareLikeButtons
                  track={actualTrack}
                  overlay={true}
                  playlistGrid={true}
                  song={song}
                />
              </Grid>
            </Box>
          </Fade>
        ) : (
          <></>
        )}

        <Box
          className="PlaylistGridCard--IconMore"
          aria-label="Mehr anzeigen"
          sx={{
            zIndex: "1000",
            //display: {xs: "block", lg: "none"},
            backgroundColor: theme.palette.secondary.main,
            position: "absolute",
            top: ".5rem",
            right: 0,
            bottom: 0,
            marginLeft: "auto",
            marginTop: 0,
            marginBottom: "auto",
            transform: "translate(35%)",
            width: "2.5rem",
            height: "2.5rem",
            border: `2px solid ${theme.palette.common.white}`,
            transition: "all .2s ease-in-out",
            "&.MuiButtonBase-root": {
              minWidth: "auto",
              borderRadius: "0",
            },
          }}
        >
          <ButtonBase
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              margin: "auto",
              width: "1.5rem",
              height: "1.5rem",
            }}
            onClick={() =>
              buttonsOpen ? setButtonsOpen(false) : setButtonsOpen(true)
            }
          >
            {buttonsOpen ? (
              <CloseIcon
                sx={{ fontSize: 32, color: theme.palette.common.white }}
              />
            ) : (
              <MoreHorizIcon
                sx={{ fontSize: 32, color: theme.palette.common.white }}
              />
            )}
          </ButtonBase>
        </Box>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          sx={{ position: "relative", zIndex: "50" }}
        >
          <Grid item xs={12} sx={{ height: "100%" }}>
            <Grid container alignItems="center" justifyContent="center">
              <Grid
                item
                className="PlaylistGridCard--BandnameWrapper"
                xs={12}
                sx={{
                  order: "1",
                  textAlign: "center",
                  textTransform: "uppercase",
                }}
              >
                <Box
                  className="PlaylistGridCard--Bandname"
                  sx={{
                    textTransform: "uppercase",
                    fontSize: ".9375rem",
                    lineHeight: "1.2",
                    fontWeight: basierCirleRegular.style.fontWeight,
                    fontFamily: basierCirleRegular.style.fontFamily,
                    textOverflow: "ellipsis",
                    width: "100%",
                    overflow: "hidden",
                    lineClamp: "2",
                    display: "-webkit-box",
                    "-webkit-line-clamp": "2",
                    "-webkit-box-orient": "vertical",
                    mb: 0.5,
                    [theme.breakpoints.up("sm")]: {
                      mb: 0.5,
                      fontSize: "1.375rem",
                      "& a": {
                        "&:hover": {
                          color: theme.palette.secondary.main,
                          opacity: "1",
                        },
                      },
                    },
                  }}
                >
                  <LinkStyled
                    href={`/musik/${song.band.slug}`}
                    label={song.band.name}
                    variant="white"
                  />
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  order: "3",
                  textAlign: "center",
                  textTransform: "uppercase",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: ".9375rem",
                    fontWeight: basierCirleBold.style.fontWeight,
                    fontFamily: basierCirleBold.style.fontFamily,
                    textOverflow: "ellipsis",
                    width: "100%",
                    overflow: "hidden",
                    lineClamp: "2",
                    display: "-webkit-box",
                    "-webkit-line-clamp": "2",
                    "-webkit-box-orient": "vertical",
                    [theme.breakpoints.up("sm")]: {
                      fontSize: "1.375rem",
                    },
                  }}
                >
                  {song.title}
                </Typography>
              </Grid>
              <Grid
                item
                className="PlaylistGridCard--IconWrapper"
                xs={12}
                sx={{
                  order: "2",
                  textAlign: "center",
                }}
              >
                <IconButton
                  aria-label="Song Abspielen"
                  className="PlaylistGridCard--PlayIcon"
                  onClick={() => {
                    handlePlaySong(song);
                  }}
                >
                  <SvgIcon
                    color="secondary"
                    sx={{
                      fontSize: 40,
                      color: theme.palette.common.white,
                      transition: "all .2s ease-in-out",
                      [theme.breakpoints.up("sm")]: {
                        fontSize: 72,
                      },
                    }}
                  >
                    {actualTrack?.id === song?.id && playerIsPlaying ? (
                      <PauseIconCircle />
                    ) : (
                      <PlayIconCircle />
                    )}
                  </SvgIcon>
                </IconButton>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  order: "4",
                  textAlign: "center",
                  [theme.breakpoints.up("sm")]: {
                    mb: 2,
                  },
                }}
              >
                <Typography
                  className="PlaylistGridCard--Genre"
                  sx={{
                    fontSize: ".625rem",
                    textTransform: "uppercase",
                  }}
                >
                  {song.band.genre?.title} aus {song.band.zipcode?.label}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Card>
    </>
  );
};
